import { useState, useCallback } from 'react';
import { useTicketRejectMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../../services/profileService/profile-api';
import { TicketRejectCommand } from '../../../../../services/ticketEasyService/commands/approval/TicketRejectCommand';

interface UseTicketRejectionProps {
  ticketId?: string;
  onSuccess?: () => void;
}

export function useTicketRejection({ ticketId, onSuccess }: UseTicketRejectionProps) {
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectSuccess, setRejectSuccess] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  
  const { data: myProfile } = useMyProfileQuery();
  const [rejectTicket] = useTicketRejectMutation();
  
  const openRejectDialog = useCallback(() => {
    setRejectDialogOpen(true);
  }, []);
  
  const closeRejectDialog = useCallback(() => {
    setRejectDialogOpen(false);
  }, []);
  
  const handleRejectSubmit = useCallback(async (comments: string) => {
    if (!ticketId) {
      setRejectError("Cannot reject message: Missing ticket ID");
      return;
    }
    
    if (!myProfile) {
      setRejectError("Cannot reject message: Missing user data");
      return;
    }
    
    setRejecting(true);
    setRejectError(null);
    
    try {
      const rejectCommand = new TicketRejectCommand({
        ticketId,
        rejector: myProfile.identity,
        rejectorEmail: myProfile.email,
        comments
      });
      
      await rejectTicket(rejectCommand).unwrap();
      setRejectSuccess(true);
      closeRejectDialog();
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Auto-hide success message after 6 seconds
      setTimeout(() => {
        setRejectSuccess(false);
      }, 6000);
    } catch (error) {
      console.error("Failed to reject ticket:", error);
      setRejectError("Failed to reject ticket. Please try again.");
    } finally {
      setRejecting(false);
    }
  }, [ticketId, myProfile, rejectTicket, closeRejectDialog, onSuccess]);
  
  const clearSuccessMessage = useCallback(() => {
    setRejectSuccess(false);
  }, []);
  
  const clearErrorMessage = useCallback(() => {
    setRejectError(null);
  }, []);
  
  return {
    rejectDialogOpen,
    rejecting,
    rejectSuccess,
    rejectError,
    openRejectDialog,
    closeRejectDialog,
    handleRejectSubmit,
    clearSuccessMessage,
    clearErrorMessage
  };
}